import { ChainId } from '@baseswapfi/sdk-core'
import axios from 'axios'
import { isSupportedChain } from 'config/constants/chains'
import { useFeeAprInfoLazyQuery } from 'graphql/thegraph/__generated__/types-and-hooks'
import { apolloClient } from 'graphql/thegraph/apollo'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import useTokenPrices from 'hooks/useTokenPrices'
import { getPoolsTVLData } from 'state/pools/fetchPools'
import useSWR from 'swr'

const MERKL_API_URL = 'https://api.angle.money/v2/merkl?chainIds[]=8453&AMMs[]=baseswap'
const merkleChainIds = [ChainId.BASE]

// Util to get 7 day list of Uni matching id's used for snapshot data in the subgraph
function getPoolDayDataIds(poolAddresses: string[]) {
  const recordIds = []
  const timestamp = Math.floor(Date.now() / 1000)
  // Round down to start of current day
  const dayID = Math.floor(timestamp / 86400)
  // Inflate back up to a block timestamp equivalent for the day start
  const dayStartTimestamp = Math.floor(dayID * 86400)
  // subtract one day from the inflated timestamp to get start of yesterday
  const dayStartYesterdayTimestamp = dayStartTimestamp - 86400
  const yesterdayDayId = Math.floor(dayStartYesterdayTimestamp / 86400)

  // Generate subgraph id for last 7 days of poolDayData
  for (const pool of poolAddresses) {
    let day = yesterdayDayId
    let count = 7
    while (count >= 0) {
      recordIds.push(`${pool.toLowerCase()}-${day}`)
      day--
      count--
    }
  }

  return recordIds
}

export default function useV3PoolAprs(poolAddresses: string[]) {
  const { chainId, library: provider } = useActiveWeb3React()
  const { getTokenPrice } = useTokenPrices()

  const [getFeeAprInfo] = useFeeAprInfoLazyQuery({
    pollInterval: 30000,
    client: apolloClient,
  })

  const getData = async () => {
    try {
      if (!chainId || !isSupportedChain(chainId)) return null

      const merklData = await axios(MERKL_API_URL)

      const swapFeeData = await getFeeAprInfo({
        variables: {
          recordIds: getPoolDayDataIds(poolAddresses),
        },
      })

      const tvlData = await getPoolsTVLData(poolAddresses, getTokenPrice, provider)

      // console.log(merklData)

      let merklePools = []
      // Will always apply right now but need accounting for multi chain then with `merkleChainIds`
      if (merklData) {
        merklePools = Object.entries(merklData.data[chainId].pools).map((obj: any) => {
          const aprs = obj[1].aprs

          const aprList = Object.entries(aprs).map((apr: any) => {
            return {
              label: apr[0] || '',
              value: `${apr[1] ? apr[1].toFixed(2) : '0.00'}%`,
            }
          })

          return {
            pool: obj[0],
            ...obj[1],
            aprs: aprList,
            rewardAPR: obj[1].meanAPR,
          }
        })
      }

      let graphPools = swapFeeData.data

      if (!graphPools) {
        console.log('useV3PoolAprs: no graph data')
        return null
      }

      // Hacky combining of tvl apr related data with swap fees and reward data
      const poolsWithTotalFees = poolAddresses.map((p) => {
        const pLower = p.toLowerCase()
        let pool: any = {
          pool: p,
        }

        const matches = graphPools.poolDayDatas.filter((sp) => sp.id.toLowerCase().includes(pLower)) || []

        let totalFeesForWeek = 0
        matches.forEach((f) => {
          totalFeesForWeek += parseFloat(f.feesUSD)
        })

        const tvlMatch = tvlData.find((p) => p.pool.toLowerCase() === pLower)

        const avgFee = totalFeesForWeek / 7
        const annualFees = avgFee * 365

        pool.sevenDayAvgFees = avgFee
        pool.estimatedAnnualFees = annualFees
        pool.feesAPR = (annualFees / tvlMatch.tvl) * 100
        pool.totalAPR = pool.feesAPR // Add merkle is needed then
        pool.feesAPRLabel = `Swap fee APR: ${pool.feesAPR.toFixed(2)}%`
        pool.tvl = tvlMatch.tvl

        const merklMatch = merklePools.find((p2) => p2.pool.toLowerCase() === pLower)

        if (merklMatch) {
          pool = {
            ...merklMatch,
            ...pool,
            totalAPR: pool.totalAPR + merklMatch.rewardAPR,
          }
        }

        pool.rewardAPR = pool.rewardAPR || 0
        pool.totalAPRLabel = `${pool.totalAPR.toFixed(2)}%`

        return pool
      })

      // console.log(poolsWithTotalFees)

      return poolsWithTotalFees
    } catch (error) {
      console.log(error)
    }
  }

  return useSWR(
    `${poolAddresses.join(',')}`,
    async () => {
      return await getData()
    },
    {
      refreshInterval: 10000,
    },
  )
}
